import React, {
  useState,
  useCallback,
  useMemo,
  useEffect,
  useRef,
} from 'react';
import * as common from '@/common';
import cache from '@/cache';
import {
  Coord,
  Dimensions,
  Crop,
} from '@t/LayoutTypes';


interface BScrollerProps {
  eye: 'OD' | 'OS';
  index: number;
  onSlice: (value: number) => void;
}
const BScroller = ({
  eye,
  index,
  onSlice,
}: BScrollerProps): JSX.Element => {
  const imgRef = useRef<SVGImageElement>(null);
  const mxRef = useRef<DOMMatrix>(null);
  const [slice, setSlice] = useState(0);

  const { BSCAN } = cache.images[eye];
  const url = BSCAN.urls[index];
  const crop = BSCAN.crop;

  const handleMouseMove = useCallback((ev: MouseEvent) => {
    const x = calc(ev.clientX, crop, mxRef.current);
    setSlice(x);
    onSlice(x);
  }, [crop, onSlice]);

  const handleMouseUp = useCallback((ev: MouseEvent) => {
    window.removeEventListener('mouseup', handleMouseUp);
    window.removeEventListener('mousemove', handleMouseMove);
  }, [handleMouseMove]);

  const handleMouseDown = useCallback((ev: MouseEvent) => {
    ev.preventDefault();
    window.addEventListener('mouseup', handleMouseUp);
    window.addEventListener('mousemove', handleMouseMove);
    mxRef.current = imgRef.current!.getScreenCTM()!.inverse();
    const x = calc(ev.clientX, crop, mxRef.current);
    setSlice(x);
    onSlice(x);
  }, [crop, onSlice, handleMouseUp, handleMouseMove]);

  useEffect(() => {
    const img = imgRef.current!;
    img.addEventListener('mousedown', handleMouseDown);
    return () => {
      img.removeEventListener('mousedown', handleMouseDown);
      window.removeEventListener('mouseup', handleMouseUp);
      window.removeEventListener('mousemove', handleMouseMove);
    };
  }, [handleMouseDown, handleMouseUp, handleMouseMove]);

  const lx = common.lerp1(slice, crop.x, crop.x + crop.width);
  const lw = Math.max(1, crop.width / 500);

  return <g>
    <image
      ref = {imgRef}
      crossOrigin='anonymous'
      href={url}
    />
    <line
      x1={lx}
      y1={0}
      x2={lx}
      y2={crop.height}
      stroke='#FF0'
      strokeWidth={lw}
      opacity={0.5}
      pointerEvents='none'
    />
  </g>
}

export default BScroller;


function calc(x: number, crop: Crop, m: DOMMatrix): number {
  const p = new DOMPoint(x, 0);
  const px = p.matrixTransform(m).x;
  const rx = common.lerp1_i(px, crop.x, crop.x + crop.width);
  return Math.max(0, Math.min(1, rx));
}
